import { useEffect, useRef, useState } from "react";
import { User, LogOut, ShieldCheck, LogIn, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

export function UserBadge() {
  const { status, user, signIn, signOut, signOutAppOnly } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const email = user?.email;
  const displayName = user?.name || email || "Sign in";
  const initials = email
    ? email
        .split("@")[0]
        .split(/[._-]/)
        .slice(0, 2)
        .map(p => p.charAt(0).toUpperCase())
        .join("") || "?"
    : "?";

  const loading = status === "checking" || status === "unknown";

  return (
    <div ref={rootRef} className="relative" data-testid="user-badge">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-2 px-2 py-1 min-h-[40px] rounded-md border border-card-border bg-card/70 hover-elevate"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={email ? `Account menu for ${email}` : "Account menu"}
        title={displayName}
        data-testid="user-badge-trigger"
      >
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/15 text-primary text-[11px] font-semibold tabular-nums">
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : email ? (
            initials
          ) : (
            <User className="w-3.5 h-3.5" />
          )}
        </span>
        <span className="hidden sm:inline text-[11.5px] font-medium leading-tight truncate max-w-[160px] text-foreground/85">
          {loading ? "Checking..." : displayName}
        </span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] z-50 w-[280px] rounded-md border border-border bg-card shadow-xl p-3"
          data-testid="user-badge-menu"
        >
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
            {loading ? "Checking session" : email ? "Signed in" : "Not signed in"}
          </div>
          <div className="text-[13px] font-medium leading-snug break-all">
            {loading ? "..." : email ?? "No Cloudflare Access session"}
          </div>
          {user?.name && user.name !== email && (
            <div className="text-[11.5px] text-muted-foreground mt-0.5">{user.name}</div>
          )}
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>
              via Cloudflare Access
              {user?.idpType && <span className="ml-1 opacity-70">({user.idpType})</span>}
            </span>
          </div>
          <div className="mt-3 pt-3 border-t border-border space-y-2">
            {email ? (
              <>
                <button
                  type="button"
                  onClick={signOut}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 min-h-[44px] rounded-md border border-border text-[13px] font-medium hover-elevate"
                  data-testid="user-badge-signout"
                >
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
                <button
                  type="button"
                  onClick={signOutAppOnly}
                  className="w-full text-[11px] text-muted-foreground hover-elevate rounded px-2 py-1.5 min-h-[32px]"
                  data-testid="user-badge-signout-app"
                  title="Per-app logout (use if the main Sign out doesn't return you here)"
                >
                  Sign out (this app only)
                </button>
                <p className="text-[10.5px] text-muted-foreground leading-snug">
                  Sign out clears your Cloudflare Access session and returns you here so a fresh sign-in is required.
                </p>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={signIn}
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 min-h-[44px] rounded-md bg-primary text-primary-foreground text-[13px] font-medium"
                  data-testid="user-badge-signin"
                >
                  <LogIn className="w-4 h-4" /> Sign in
                </button>
                <p className="text-[10.5px] text-muted-foreground leading-snug">
                  Cloudflare Access protects this site. Click Sign in to start the email OTP flow.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
